/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Dropdown, Menu, Tag } from 'antd';
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';
import { DropDownProps } from 'antd/lib/dropdown';

// local imports
import { useStyles } from '~src/hooks/useStyles';
import { DataContextMenuItemType } from '~src/data/dataContextMenu';
import IconButton from '~components/_common/IconButton/IconButton';
import { DbFile, DbFolder, DbFocusOptions, DbModuleFile } from '~src/types/db';
import theme from '~src/styles/themes/js/light-theme';
import ModuleTenderHammer from '~src/assets/nav-icons/module-tender-hammer.svg';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { styleFn } from './styles';

export interface BaseRightClickOverlayProps extends Partial<DropDownProps> {
  items: DataContextMenuItemType[];
  onItemClick: (item: DataContextMenuItemType, option?: number) => void;
  onOverlayClose: () => void;
  selected?: { folder?: DbFolder; file?: DbFile; moduleFile?: DbModuleFile };
  leftSide?: boolean;
  focusOptions?: DbFocusOptions[];
  showHeader?: boolean;
  headerIcon?: [IconPrefix, IconName];
  headerTitle?: string;
  headerIconColor?: string;
}
/** Resource rightclick (context) menu; */
export const BaseRightClickOverlay: React.FC<BaseRightClickOverlayProps> = ({
  items,
  onItemClick,
  children,
  onOverlayClose,
  selected,
  leftSide,
  focusOptions,
  showHeader,
  headerIcon,
  headerTitle,
  headerIconColor,
  ...dropdownProps
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();

  const renderHeader = () => {
    if (!showHeader || !headerTitle) return null;

    return (
      <div css={styles.headerContainer}>
        <Tag css={styles.header}>
          {headerIcon && (
            <IconButton
              icon={headerIcon}
              customStyle={{
                ...styles.headerIcon,
                ...(headerIconColor && {
                  color: `${headerIconColor} !important`,
                }),
              }}
              backgroundOff
            />
          )}
          <span css={styles.headerTitle}>{headerTitle}</span>
        </Tag>
      </div>
    );
  };

  const renderMenuItem = (item: DataContextMenuItemType) => {
    return item.isSubMenu && focusOptions ? (
      <Menu.SubMenu
        disabled={item.disabled}
        icon={
          <IconButton
            icon={[item.iconStyle as IconPrefix, item.icon as IconName]}
            customStyle={styles.icon}
            backgroundOff
          />
        }
        key={item.key}
        css={styles.item}
        title={
          <p css={styles.text}>{intl.formatMessage({ id: item.name })}</p>
        }>
        <Menu.Item
          key={'subMenuNoHighLight'}
          css={styles.itemFocus}
          onClick={() => onItemClick(item, -1)}>
          <span
            css={[
              styles.text,
              {
                marginBottom: '5px',
                borderBottom: `1px solid ${theme.colors.lightGray}`,
              },
            ]}>
            {intl.formatMessage({ id: 'no-highlight' })}
          </span>
        </Menu.Item>
        {focusOptions?.map((option) => {
          return (
            <Menu.Item
              key={option.value + 'subMenu'}
              css={styles.itemFocus}
              onClick={() => onItemClick(item, option.value)}>
              <div
                css={[
                  styles.colorItemContainer,
                  {
                    border:
                      selected?.folder?.focus === option.value
                        ? `1.5px solid ${theme.colors.primary}`
                        : '',
                  },
                ]}>
                <div
                  css={[
                    styles.colorItem,
                    { background: `#${option.color_hex_code}` },
                  ]}
                />
              </div>
            </Menu.Item>
          );
        })}
        <Menu.Item
          key={'subMenuNoHighLightIcon'}
          css={styles.itemFocus}
          onClick={() => onItemClick(item, -1)}>
          <span
            css={[
              styles.text,
              {
                width: '100%',
                marginTop: '5px',
                borderTop: `1px solid ${theme.colors.lightGray}`,
              },
            ]}>
            <IconButton
              iconStyle="far"
              icon="info-circle"
              customStyle={styles.icons}
              backgroundOff
              tooltip={
                intl.locale === 'da' ? (
                  <div>
                    <h3 css={styles.whiteHeader}>Mappe Highlight farver</h3>
                    <p>
                      Her kan der vælges om enkelte mapper skal have mere
                      fremtrædende synlighed ved at give dem en bestemt
                      baggrundsfarve.
                    </p>
                    <p>
                      <b>Rettigheder</b>
                      <br />
                      Alle brugere med redigeringsrettigheder til en mappe kan
                      vælge mappe highlight farver.
                    </p>
                    <p>
                      <b>Note</b>
                      <br />
                      Vær opmærksom på at de valgte farver vil være synlige for
                      alle brugere på projektet. Mappe Highlight er en funktion
                      der giver de projektstyrende mulighed for at give enkelte
                      mapper ekstra fremtrædende synlighed for både dem selv og
                      for alle andre på projektet.
                      <br />
                      Hvis det ønskes at enkelte mapper bliver fremtrædende for
                      en selv, kan Favorit funktionaliteten benyttes.
                    </p>
                  </div>
                ) : (
                  <div>
                    <h3 css={styles.whiteHeader}>Folder Highlights</h3>
                    <p>
                      A folder can be highlighted in order to make it visually
                      more accessible for everyone. This functionality will add
                      a background color to the selected folder.
                    </p>
                    <p>
                      <b>Permissions</b>
                      <br />
                      All users with the permission to edit the folder have
                      permissions to edit the selected highlight color.
                    </p>
                    <p>
                      <b>Note</b>
                      <br />
                      Be aware that selected highlight colors are visible for
                      all users on the project. The Folder Highlight
                      functionality will give Project Managers the opportunity
                      to make specific folders more visible for everyone.
                      <br />
                      To get easier access to specific folders on your own
                      account, use the Favorites functionality.
                    </p>
                  </div>
                )
              }
              widthTooltip="500px"
            />
          </span>
        </Menu.Item>
      </Menu.SubMenu>
    ) : (
      <Menu.Item
        key={item.key}
        css={styles.item}
        disabled={item.disabled}
        title={
          item.disabled
            ? item.disabledTooltip
              ? intl.formatMessage({ id: item.disabledTooltip })
              : intl.formatMessage({ id: 'permission-restricted' })
            : undefined
        }
        onClick={() => onItemClick(item)}>
        {item.name === 'add-to-tender' ? (
          <NavIcon
            customStyle={{
              width: '26px',
              height: '17px',
              marginRight: '7px',
            }}
            icon={ModuleTenderHammer}
          />
        ) : (
          <IconButton
            icon={[item.iconStyle as IconPrefix, item.icon as IconName]}
            customStyle={styles.icon}
            backgroundOff
          />
        )}
        <span css={styles.text}>
          {item.displayName || intl.formatMessage({ id: item.name })}
        </span>
      </Menu.Item>
    );
  };

  const hasGroups = items.some(
    (item) => item.groupKey && item.groupKey !== 'root'
  );
  let menuChildren;

  if (hasGroups) {
    const groupsMap = new Map<string, DataContextMenuItemType[]>();

    items.forEach((item) => {
      const key = item.groupKey || 'root';
      if (!groupsMap.has(key)) {
        groupsMap.set(key, []);
      }
      groupsMap.get(key)!.push(item);
    });

    menuChildren = Array.from(groupsMap.entries()).map(
      ([groupKey, groupItems]) => {
        // If it's the root group, map the items normally without a header
        if (groupKey === 'root') {
          return groupItems.map(renderMenuItem);
        }

        // Render grouped items wrapped in a Menu.ItemGroup
        return (
          <Menu.ItemGroup
            key={groupKey}
            title={
              <span
                style={{ fontWeight: 600, color: '#262626', fontSize: '13px' }}>
                {intl.formatMessage({
                  id: groupKey,
                })}
              </span>
            }>
            {groupItems.map(renderMenuItem)}
          </Menu.ItemGroup>
        );
      }
    );
  } else {
    // Fallback
    menuChildren = items.map(renderMenuItem);
  }

  const menu = (
    <Menu css={styles.menu}>
      {renderHeader()}
      {menuChildren}
    </Menu>
  );

  return (
    <Dropdown
      onVisibleChange={(visible) => {
        if (!visible) onOverlayClose();
      }}
      overlay={menu}
      //visible={!!selected?.file || !!selected?.folder}
      trigger={['contextMenu']}
      {...dropdownProps}>
      <div
        id="mask"
        className="site-dropdown-context-menu"
        tabIndex={0} // Make the div focusable
        onTouchEnd={(e) => {
          // Handle touch events
          e.preventDefault();
          // You might need to toggle the visibility of the dropdown manually here
        }}>
        {children}
      </div>
    </Dropdown>
  );
};
